(ns minorodata.components.sql-console.data-preview
  (:require ["copy-to-clipboard" :as copy]
            ["papaparse" :as papaparse]
            ["react" :as react]
            ["react-apexcharts" :default Chart]
            ["react-resize-detector" :refer [useResizeDetector]]
            [applied-science.js-interop :as js-interop]
            [clojure.string]
            [lucide-react :refer [AreaChart ChevronDown ClipboardCopy Download
                                  Sheet]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.utils :refer [save-buffer-to-file]]
            [minorodata.components.sql-console.result-copy :as result-copy]
            [ui.data-grid.preview :as data-grid-preview]
            [minorodata.sql-console.model :refer [->sql-editor]]
            [re-posh.core :as re-posh]
            [ui.button :refer [button]]
            [ui.dropdown-menu :refer [dropdown-menu dropdown-menu-item]]
            [ui.fields :as fields]
            [ui.tabs]))


(defn toggle-buttons [{:keys [value on-change]}]
  [ui.tabs/tabs
   {:value           value
    :size            :small
    :tabs            [{:value "table"
                       :label [:> Sheet {:size "15"}]}
                      {:value "chart"
                       :label [:> AreaChart {:size "15"}]}]
    :on-value-change on-change}])

(defn data-preview-table [{:keys [tab-configuration update-configuration current-tabbed-data]}]
  (let [current-view-type (get tab-configuration :view-type "table")
        preview-height    @(re-posh/subscribe [:sql-editor/prop :sql-editor/data-preview-height])
        dark-mode?        @(re-posh/subscribe [:app/prop :app/dark-mode?])
        menu-height       87
        height-expression (if (some? preview-height)
                            (str "calc(" (js/Math.ceil preview-height) "px - " menu-height "px)")
                            (str "calc(50vh - " menu-height "px)"))

        grid-api-ref (react/useRef nil)

        [sorted-titles sorted-data]
        (react/useMemo
         (fn []
           (let [{:keys [indexed-headers indexed-data]} current-tabbed-data
                 fields-getter (mapv (fn [{:keys [field]}] #(get % field)) indexed-headers)]
             [(mapv :title indexed-headers)
              (mapv (apply juxt fields-getter) indexed-data)]))
         #js [current-tabbed-data])

        no-data? (empty? (:indexed-data current-tabbed-data))

        rows-to-copy (fn []
                       (data-grid-preview/displayed-rows
                        (js-interop/get grid-api-ref :current)
                        (count sorted-titles)
                        sorted-data))

        copy-csv (fn copy-csv-fn []
                   (copy (result-copy/csv sorted-titles (rows-to-copy))
                         #js {:format "text/plain"})
                   (dispatch [:app.events/add-snack :success "Copied to clipboard"]))

        copy-tsv (fn copy-tsv-fn []
                   (copy (result-copy/tsv (rows-to-copy))
                         #js {:format "text/plain"})
                   (dispatch [:app.events/add-snack :success "Copied to clipboard"]))

        download-file (fn download-fn []
                        (save-buffer-to-file (js-interop/call papaparse :unparse
                                                              (clj->js {:fields sorted-titles :data sorted-data})
                                                              #js {:newline "\r\n"})
                                             (str  "kleene-export-" (.toISOString (js/Date.)) ".csv")))]

    [:<>
     [:div {:class "py-2 px-2 flex items-center justify-between"}
      [:div {:class "flex gap-2 items-center"}
       [toggle-buttons {:value     current-view-type
                        :on-change #(update-configuration :view-type %)}]

       [:span {:class "ml-6! text-kleene-text/50"}
        "Preview table:"]


       [:div
        [button {:size       :small
                 :variant    :secondary
                 :data-cy    "sql-console-copy-btn"
                 :data-index 0
                 :disabled   no-data?
                 :on-click   copy-tsv
                 :icon       ClipboardCopy
                 :icon-props {}
                 :class      "rounded-r-none!"}
         "Copy"]

        [dropdown-menu {:content [:div
                                  [dropdown-menu-item {:on-click copy-csv
                                                       :class "flex w-full justify-between"
                                                       :data-cy  "sql-console-copy-csv-btn"}
                                   [:div
                                    "CSV"
                                    [:div {:class "text-kleene-text/50 text-xs"}
                                     "Includes headers"]]]
                                  [dropdown-menu-item {:on-click copy-tsv
                                                       :data-cy  "sql-console-copy-tsv-btn"}
                                   [:div
                                    "TSV"
                                    [:div {:class "text-kleene-text/50 text-xs"}
                                     "No headers, for spreadsheets"]]]]}

         [:div {:class "inline-block h-8"}
          [button {:size       :small
                   :variant    :secondary
                   :data-cy    "sql-console-copy-dropdown-btn"
                   :data-index 0
                   :disabled   no-data?
                   :class      "rounded-l-none! -ml-px h-full"
                   :icon       ChevronDown
                   :icon-props {:class "h-full"}}]]]]


       [button {:size       :small
                :variant    :secondary
                :data-cy    "sql-console-download-btn"
                :data-index 0
                :disabled   no-data?
                :on-click   download-file
                :icon       Download
                :icon-props {:fontSize "small"}}
        "Download CSV"]]

      (when-not no-data?
        [:div {:class "inline-block"}
         [:span {:class "ml-6! text-kleene-text/60"}
          (str (count sorted-data)
               (if (= 1 (count sorted-data))
                 " row"
                 " rows"))]])]

     (if no-data?
       [:div {:class "p-4 text-center"}
        "No records to display"]
       [:f> data-grid-preview/table
        {:headers          sorted-titles
         :rows             sorted-data
         :dark-mode?       dark-mode?
         :height           height-expression
         :column-width     150
         :sortable?        true
         :filterable?      true
         :resizable?       true
         :row-numbers?     true
         :header-tooltips? true
         :ensure-dom-order? true
         :on-grid-ready    #(js-interop/assoc! grid-api-ref :current
                                               (js-interop/get % :api))}])]))




(defn data-preview-chart [{:keys [tab-configuration update-configuration current-tabbed-data]}]
  (let [dark-mode?        @(re-posh/subscribe [:app/prop :app/dark-mode?])

        current-view-type (get tab-configuration :view-type "table")
        chart-type        (get tab-configuration :chart-type "line")
        xaxis             (get tab-configuration :xaxis)
        data-column       (get tab-configuration :data-column)
        select-data       (fn [data header]
                            (map #(get % header nil)
                                 data))

        {:keys [data headers]} current-tabbed-data]
    [:<>
     [:div {:class "p-2 grow-0 shrink-0 basis-64"}
      [:div {:class "mt-px"}
       [toggle-buttons {:value current-view-type
                        :on-change #(update-configuration :view-type %)}]]

      [:div {:class "flex flex-col gap-4 mt-8"}
       [:div
        [fields/select
         {:field {:value  chart-type
                  :action (fn [value]
                            (update-configuration :chart-type value))}

          :errors        nil
          :full-width    true
          :id            :chart-type
          :variant       "standard"
          :label         "Chart type"
          :native        false

          :options [{:value "line" :text "Line chart"}
                    {:value "bar" :text "Bar chart"}
                    {:value "area" :text "Area chart"}]}]]

       [:div
        [fields/select
         {:field {:value  xaxis
                  :action (fn [value]
                            (update-configuration :xaxis value))}

          :errors        nil
          :full-width    true
          :id            :chart-xaxis
          :variant       "standard"
          :label         "X axis"
          :placeholder   "Select column"
          :native        false
          :options       (map (fn [{:keys [title field]}] {:value field :text title})
                              headers)}]]

       [:div
        [fields/select
         {:field {:value  data-column
                  :action (fn [value]
                            (update-configuration :data-column value))}

          :errors        nil
          :full-width    true
          :id            :chart-data-column
          :variant       "standard"
          :label         "Data column"
          :placeholder   "Select column"
          :native        false
          :options       (map (fn [{:keys [title field]}] {:value field :text title})
                              headers)}]]]]

     [:div {:class "grow"}
      [:> Chart {:options {:chart {:id "sql-console"
                                   :toolbar {:export {:csv {:headerCategory xaxis}}}
                                   :background "rgba(0, 0, 0, 0)"}
                           :xaxis {:categories (select-data data xaxis)}
                           :colors ["#558554" "#1A799E" "#3D5AFE" "#00C853" "#F44336" "#FF9100"]
                           :theme {:mode (if dark-mode? "dark" "light")}}
                 :type (get tab-configuration :chart-type "line")
                 :height "100%"
                 :width "100%"
                 :series [{:name data-column
                           :data (select-data data data-column)}]}]]]))


(defn data-preview [{:keys [classes]}]

  (let [tabbed-data          @(re-posh/subscribe [:sql-console.data/tabbed-data])
        tab                  (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/results-tab]) 0)
        all-tab-configs      @(re-posh/subscribe [:sql-editor/prop :sql-editor/results-tab-configs])

        tab-configuration    (get all-tab-configs tab {})
        current-view-type    (get tab-configuration :view-type "table")

        update-configuration (fn [k v]
                               (let [new-tab-configs (assoc-in all-tab-configs [tab k] v)]
                                 (dispatch [:sql-editor/update {:sql-editor/results-tab-configs new-tab-configs}])))

        query-start-time    @(re-posh/subscribe [:sql-editor/prop :sql-editor/query-start-time])

        current-tabbed-data  (nth tabbed-data tab [])
        set-tab              (fn [_ id]
                               (dispatch-sync [:sql-editor/update (->sql-editor {:results-tab id})]))

        {:keys [ref]} (js->clj (useResizeDetector #js {:handleWidth  true
                                                       :handleHeight true
                                                       :refreshMode  "throttle"
                                                       :refreshRate  100
                                                       :onResize     (fn [params]
                                                                       (let [{:keys [height]} (js->clj params :keywordize-keys true)]
                                                                         (dispatch [:sql-editor/update (->sql-editor {:data-preview-height height})])
                                                                         (dispatch [:sql-console.events/save-config-to-local-storage])))})
                               :keywordize-keys true)]
    [:div {:class "flex flex-col h-full grow"
           :ref ref}
     [ui.tabs/tabs
      {:value           tab
       :variant         :underlined
       :on-value-change #(set-tab nil %)
       :tabs
       (concat (mapv
                (fn [idx]
                  {:value idx
                   :label [:span (str "Result " (inc idx))]})
                (range (count tabbed-data))))}]

     (when (= "chart" current-view-type)
       [:div {:class "flex flex-row gap-4 grow overflow-scroll"}
        [data-preview-chart {:classes              classes
                             :tab-configuration    tab-configuration
                             :update-configuration update-configuration
                             :current-tabbed-data  current-tabbed-data}]])

     (when (= "table" current-view-type)
       ^{:key (str tab-configuration query-start-time)}
       [:f> data-preview-table
        {:classes              classes
         :tab-configuration    tab-configuration
         :update-configuration update-configuration
         :current-tabbed-data  current-tabbed-data}])]))
